import { combineReducers, type Reducer } from 'redux';

import type { ApiError, Page } from 'modules/global/generic.types';
import error from 'modules/global/reducers/generic.error';
import hasMore from 'modules/global/reducers/generic.hasMore';
import ids from 'modules/global/reducers/generic.ids';
import loading from 'modules/global/reducers/generic.loading';
import page from 'modules/global/reducers/generic.page';
import type { GlobalConfig, ReducerFunction } from 'modules/global/reducers/reducers.types';
import type { Nullable } from 'modules/global/typing/common/common';

/** The five slices every paginated search owns. */
export type SimpleSearchState = {
  ids: string[];
  error: Nullable<ApiError>;
  page: Page;
  hasMore: boolean;
  loading: boolean;
};

/** Extra handlers for one of the five slices, keyed by action type. */
type SpecificReducers<U> = Record<string, ReducerFunction<U>>;

type SimpleSearchConfig<T> = {
  global?: GlobalConfig<T>;
  ids?: SpecificReducers<string[]>;
  error?: SpecificReducers<Nullable<ApiError>>;
  page?: SpecificReducers<Page>;
  hasMore?: SpecificReducers<boolean>;
  loading?: SpecificReducers<boolean>;
};

/**
 * Assembles the five slices of a paginated search around one action root, plus any slice a module
 * adds of its own (a `sort` reducer, …) — those are part of the returned state, which is what lets
 * a caller type the whole slice without an assertion.
 * @template T the entity the SUCCESS payload carries (`generic.ids` maps it to ids)
 * @template A the module's additional reducers — their slices are inferred into the result state
 * @example
 *  // React to INTERVENTIONS_FETCH(REQUEST, SUCCESS and FAILURE), only when the source matches
 *  createSearchReducers(types.INTERVENTIONS_FETCH, {
 *    global: { matcher: ({ meta }) => meta?.source === 'importChecklistGroup' },
 *  })
 */
const createSearchReducers = <T = { id: string }, A extends Record<string, Reducer> = Record<never, never>>(
  actionRoot: string,
  { global, ...specific }: SimpleSearchConfig<T> = {},
  additionalReducers = {} as A
) =>
    combineReducers({
      ids: ids<T>(actionRoot, { global, reducers: specific.ids }),
      error: error<ApiError>(actionRoot, { global, reducers: specific.error }),
      page: page(actionRoot, { global, reducers: specific.page }),
      hasMore: hasMore(actionRoot, { global, reducers: specific.hasMore }),
      loading: loading(actionRoot, { global, reducers: specific.loading }),
      ...additionalReducers,
    });

export default createSearchReducers;
